import {
  BoardRevisionDiff,
  BomDiffStatus,
  ElementPlacement,
} from "../../../reducers/uploadedBoard";
import {
  COLORS,
  FlashPCBTable,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../../../constants/Styles";
import React, { ReactNode, useState } from "react";
import {
  BomDiffContainer,
  CheckboxContainer,
  DiffCell,
} from "./RevisionsStyles";
import usePart from "../../../utils/hooks/commonly-stored/parts";
import LoadingIcon from "../../common/LoadingIcon";
import Checkbox from "../../common/Checkbox";
import CannotProcessDiff from "./CannoProcessDiff";
import OffsetDetails from "./OffsetDetails";

function DiffRow({
  fieldName,
  previousValue,
  value,
  renderValue,
}: {
  fieldName: string;
  previousValue?: string | null;
  value?: string | null;
  renderValue?: (value: string | null) => ReactNode;
}) {
  const isDifferent = previousValue !== value;
  return (
    <TableRow>
      <DiffCell color={COLORS.secondaryLight}>{fieldName}</DiffCell>
      {previousValue === undefined ? (
        <DiffCell />
      ) : (
        <DiffCell backgroundColor={isDifferent ? COLORS.warn : undefined}>
          {renderValue ? renderValue(previousValue) : previousValue}
        </DiffCell>
      )}
      {value === undefined ? (
        <DiffCell />
      ) : (
        <DiffCell backgroundColor={isDifferent ? COLORS.standard : undefined}>
          {renderValue ? renderValue(value) : value}
        </DiffCell>
      )}
    </TableRow>
  );
}

function boolToString(value?: boolean): string | undefined {
  if (value === undefined) {
    return value;
  }
  return value ? "True" : "False";
}

function Part({ partId }: { partId?: string | null }) {
  const { part, loading } = usePart(partId || null);

  if (!partId) {
    return null;
  }
  if (loading) {
    return <LoadingIcon />;
  }
  if (part === null) {
    return "Unknown part";
  }
  return `${part.name} ${part.manufacturer && `(${part.manufacturer})`}`;
}

function placementToString(
  placement?: ElementPlacement | null,
): string | null | undefined {
  if (placement) {
    return `(${placement.x.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${placement.unit}, ${placement.y.toLocaleString(
      undefined,
      {
        maximumFractionDigits: 2,
      },
    )} ${placement.unit}) ${placement.rotation_degrees}°, ${placement.mount_type}`;
  }
  return placement;
}

export default function BomDiff({ diff }: { diff: BoardRevisionDiff | null }) {
  const [showAdditions, setShowAdditions] = useState(true);
  const [showUpdates, setShowUpdates] = useState(true);
  const [showDeletions, setShowDeletions] = useState(true);
  const [showNoChange, setShowNoChange] = useState(true);

  if (diff === null) {
    return <CannotProcessDiff />;
  }

  const itemsToShow = diff.bom.filter((bomDiffItem) => {
    if (!showAdditions && bomDiffItem.diff_status == BomDiffStatus.ADDITION) {
      return false;
    }
    if (!showUpdates && bomDiffItem.diff_status == BomDiffStatus.UPDATE) {
      return false;
    }
    if (!showDeletions && bomDiffItem.diff_status == BomDiffStatus.DELETION) {
      return false;
    }
    if (!showNoChange && bomDiffItem.diff_status == BomDiffStatus.NO_CHANGE) {
      return false;
    }
    return true;
  });

  return (
    <BomDiffContainer>
      <OffsetDetails offset={diff.previous_board_offset} />
      <CheckboxContainer>
        <Checkbox
          checked={showAdditions}
          onChange={() => setShowAdditions(!showAdditions)}
          label={"Show additions"}
        />
        <Checkbox
          checked={showUpdates}
          onChange={() => setShowUpdates(!showUpdates)}
          label={"Show updates"}
        />
        <Checkbox
          checked={showDeletions}
          onChange={() => setShowDeletions(!showDeletions)}
          label={"Show deletions"}
        />
        <Checkbox
          checked={showNoChange}
          onChange={() => setShowNoChange(!showNoChange)}
          label={"Show no change"}
        />
      </CheckboxContainer>
      {itemsToShow.length > 0 ? (
        <FlashPCBTable>
          <thead>
            <TableHeaderRow>
              <TableHeaderCell width={"8rem"}>Designator</TableHeaderCell>
              <TableHeaderCell width={"8rem"}>Status</TableHeaderCell>
              <TableHeaderCell>Diff</TableHeaderCell>
            </TableHeaderRow>
          </thead>
          <tbody>
            {itemsToShow.map((bomDiffItem) => {
              return (
                <TableRow
                  key={
                    bomDiffItem.name +
                    bomDiffItem.previous_state?.id +
                    bomDiffItem.state?.id
                  }
                >
                  <TableCell>{bomDiffItem.name}</TableCell>
                  <DiffCell>{bomDiffItem.diff_status}</DiffCell>
                  <TableCell>
                    <FlashPCBTable style={{ margin: "1rem" }}>
                      <thead>
                        <TableHeaderRow>
                          <TableHeaderCell width={"8rem"}>
                            Field
                          </TableHeaderCell>
                          <TableHeaderCell>Previous revision</TableHeaderCell>
                          <TableHeaderCell>Current revision</TableHeaderCell>
                        </TableHeaderRow>
                      </thead>
                      <tbody>
                        <DiffRow
                          fieldName={"Value text"}
                          previousValue={bomDiffItem.previous_state?.value_text}
                          value={bomDiffItem.state?.value_text}
                        />
                        <DiffRow
                          fieldName={"Do not populate"}
                          previousValue={boolToString(
                            bomDiffItem.previous_state?.do_not_populate,
                          )}
                          value={boolToString(
                            bomDiffItem.state?.do_not_populate,
                          )}
                        />
                        <DiffRow
                          fieldName={"Consignment"}
                          previousValue={boolToString(
                            bomDiffItem.previous_state?.is_consignment,
                          )}
                          value={boolToString(
                            bomDiffItem.state?.is_consignment,
                          )}
                        />
                        <DiffRow
                          fieldName={"Part selection"}
                          previousValue={bomDiffItem.previous_state?.part_id}
                          value={bomDiffItem.state?.part_id}
                          renderValue={(partId) => <Part partId={partId} />}
                        />
                        <DiffRow
                          fieldName={"Placement"}
                          previousValue={placementToString(
                            bomDiffItem.previous_state?.placement,
                          )}
                          value={placementToString(
                            bomDiffItem.state?.placement,
                          )}
                        />
                      </tbody>
                    </FlashPCBTable>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </FlashPCBTable>
      ) : (
        <div>Nothing to show</div>
      )}
    </BomDiffContainer>
  );
}
